import { EVENTS } from "../data/events";

let lastEventSol = -Infinity;

/**
 * Evaluates whether a random event should trigger during this tick.
 *
 * @param {Object} state - Store state (containing sol, buildings, resources, etc.)
 * @param {number} dt - Delta time in seconds for the tick (default 0.1s)
 * @returns {Object|null} Null if no event triggers, or { event, effect }
 */
export function maybeTriggerEvent(state, dt = 0.1) {
  if (!state) return null;

  const currentSol = typeof state.sol === "number" ? state.sol : 1;

  // Check state-level or module-level last event sol for cooldown
  const previousSol = typeof state.lastEventSol === "number" ? state.lastEventSol : lastEventSol;

  // Cooldown requirement: minimum 2 sols between events
  if (currentSol - previousSol < 2.0) {
    return null;
  }

  // Base chance: 2% per sol, scaling up slightly with more sols
  // e.g. Sol 1 = 2%, Sol 2 = 2.5%, Sol 5 = 4%, capped at 10% per sol
  const perSolChance = Math.min(0.10, 0.02 + Math.max(0, currentSol - 1) * 0.005);

  // Convert per-sol probability to per-tick probability using sol length (480s by default)
  const solLenSec = typeof state.solLenSec === "number" ? state.solLenSec : 480;
  const tickChance = perSolChance * (dt / solLenSec);

  // Roll random chance for trigger
  if (Math.random() >= tickChance) {
    return null;
  }

  // Filter events by minSol
  const eligibleEvents = EVENTS.filter((e) => typeof e.minSol === "number" ? currentSol >= e.minSol : true);

  if (eligibleEvents.length === 0) {
    return null;
  }

  // Weighted random selection
  const totalWeight = eligibleEvents.reduce((sum, e) => sum + (e.weight || 10), 0);
  let roll = Math.random() * totalWeight;
  let selectedEvent = null;

  for (const event of eligibleEvents) {
    roll -= (event.weight || 10);
    if (roll <= 0) {
      selectedEvent = event;
      break;
    }
  }

  if (!selectedEvent) {
    selectedEvent = eligibleEvents[eligibleEvents.length - 1];
  }

  // Update internal cooldown tracker
  lastEventSol = currentSol;

  // Execute effect function to generate modifications
  const effect = typeof selectedEvent.effect === "function" ? selectedEvent.effect(state) : {};

  return {
    event: selectedEvent,
    effect,
  };
}

/**
 * Reset or override the last event sol tracker (useful for testing or state reloads)
 */
export function setLastEventSol(sol) {
  lastEventSol = sol;
}

export function getLastEventSol() {
  return lastEventSol;
}

export class EventManager {
  static maybeTriggerEvent(state, dt) {
    return maybeTriggerEvent(state, dt);
  }

  static setLastEventSol(sol) {
    setLastEventSol(sol);
  }

  static getLastEventSol() {
    return getLastEventSol();
  }
}

export default EventManager;
